/**
 * PointLight2D Component
 *
 * Omnidirectional 2D point light with radius and falloff.
 * Supports both direct color and temperature-based emission modes.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  executeInEditor,
  field,
  customEditor,
} from "../attributes";

@component("core.PointLight2D")
@customEditor("PointLight2DEditor")
@displayName("Point Light 2D")
@icon("Lightbulb")
@color("#ffd43b")
@category("Lighting")
@allowMultiple(true)
@requireComponent(["core.Transform"])
@executeInEditor(true)
export class PointLight2D {
  // ── Emission Mode ──
  @field({ type: "string", label: "Emission Mode", tooltip: "Color (direct RGB) or Temperature (Kelvin)" })
  emissionMode: string = "color";

  // ── Color Mode ──
  @field({ type: "color", label: "Color", tooltip: "Light color (linear). Used in Color mode." })
  color: { r: number; g: number; b: number; a: number } = { r: 255, g: 255, b: 255, a: 255 };

  // ── Temperature Mode ──
  @field({ type: "f32", label: "Temperature", min: 1000, max: 40000, step: 50, tooltip: "Color temperature in Kelvin (1000K warm → 40000K cool)" })
  temperature: number = 6500;

  @field({ type: "f32", label: "Tint", min: -1, max: 1, step: 0.01, tooltip: "Green–Magenta tint offset (−1 green, +1 magenta)" })
  tint: number = 0;

  // ── Shared ──
  @field({ type: "f32", label: "Intensity", min: 0, max: 100, step: 0.1, tooltip: "Brightness multiplier" })
  intensity: number = 1.0;

  @field({ type: "f32", label: "Radius", min: 0, step: 1, tooltip: "Maximum range in world units" })
  radius: number = 256;

  @field({ type: "f32", label: "Falloff", min: 0.1, max: 10, step: 0.1, tooltip: "Attenuation curve exponent (1=linear, 2=quadratic)" })
  falloff: number = 2.0;

  @field({ type: "bool", label: "Enabled" })
  enabled: boolean = true;

  // ── Shadows ──
  @field({ type: "bool", label: "Cast Shadows", tooltip: "Whether this light casts shadows from shadow caster geometry" })
  castShadows: boolean = false;

  @field({ type: "f32", label: "Shadow Softness", min: 0, max: 1, step: 0.05, tooltip: "0 = hard crisp shadows, 1 = very soft penumbra" })
  shadowSoftness: number = 0.5;

  @field({ type: "f32", label: "Shadow Bias", min: 0, max: 2, step: 0.05, tooltip: "Bias to prevent shadow acne" })
  shadowBias: number = 0.01;

  // ── IES Profile ──
  @field({ type: "string", label: "IES Profile", tooltip: "IES photometric profile asset for angular light distribution" })
  iesProfile: string = "";

  @field({ type: "f32", label: "IES Intensity", min: 0, max: 1, step: 0.01, tooltip: "How strongly the IES profile modulates light distribution (0 = ignore, 1 = full)" })
  iesIntensity: number = 1.0;

  @field({ type: "f32", label: "IES Rotation", min: 0, max: 360, step: 1, tooltip: "Rotation of the IES pattern in degrees" })
  iesRotation: number = 0;

  // ── Volumetric Lighting ──
  @field({ type: "f32", label: "Volumetric Intensity", min: 0, max: 2, step: 0.01, tooltip: "God rays / light scattering intensity (0 = disabled)" })
  volumetricIntensity: number = 0;

  @field({ type: "f32", label: "Volumetric Scattering", min: 0.01, max: 1, step: 0.01, tooltip: "Fog density / scattering coefficient" })
  volumetricScattering: number = 0.1;

  // ── Sorting ──
  @field({ type: "i32", label: "Sorting Layer" })
  sortingLayer: number = 0;
}
